import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CreateLeaseRequest,
  EndLeaseRequest,
  LeaseListItem,
  LeaseSearchParams,
  LeaseWithDetails,
  UpdateLeaseRequest
} from '../types';
import { leasesApi } from '../services/leaseApi';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { debounce } from '../../../../utils/validation';

export const useLeases = () => {
  const [leases, setLeases] = useState<LeaseListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [searchParams, setSearchParams] = useState<LeaseSearchParams>({
    search: '',
    page: 1,
    limit: 20,
    sorts: [{ field: 'renewalDate', direction: 'asc' }]
  });

  const fetchLeases = useCallback(async (params: LeaseSearchParams = {}) => {
    setLoading(true);
    setError(null);
    startLoading('fetch-leases', 'Loading leases...');

    try {
      const response = await leasesApi.getAll(params);
      setLeases(response.leases);
      setPagination({
        page: params.page ?? searchParams.page ?? 1,
        limit: params.limit ?? searchParams.limit ?? 20,
        total: response.total,
        totalPages: Math.ceil(response.total / (params.limit ?? searchParams.limit ?? 20))
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leases');
    } finally {
      setLoading(false);
      stopLoading('fetch-leases');
    }
  }, [startLoading, stopLoading, searchParams.page, searchParams.limit]);

  const debouncedFetchLeases = useMemo(() => debounce(fetchLeases, 300), [fetchLeases]);

  useEffect(() => {
    fetchLeases(searchParams);
  }, []);

  const updateSearchParams = useCallback((newParams: Partial<LeaseSearchParams>) => {
    setSearchParams((prevParams) => {
      const updatedParams = { ...prevParams, ...newParams };

      if (newParams.search !== undefined || newParams.fields !== undefined) {
        updatedParams.page = 1;
        debouncedFetchLeases(updatedParams);
      } else if (newParams.sorts !== undefined) {
        updatedParams.page = 1;
        fetchLeases(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        fetchLeases(updatedParams);
      } else {
        fetchLeases(updatedParams);
      }

      return updatedParams;
    });
  }, [debouncedFetchLeases, fetchLeases]);

  const createLease = useCallback(async (leaseData: CreateLeaseRequest): Promise<LeaseWithDetails> => {
    startLoading('create-lease', 'Creating lease...');
    try {
      const response = await leasesApi.create(leaseData);
      await fetchLeases(searchParams);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create lease';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('create-lease');
    }
  }, [fetchLeases, searchParams, startLoading, stopLoading]);

  const updateLease = useCallback(async (id: number, leaseData: UpdateLeaseRequest): Promise<LeaseWithDetails> => {
    startLoading('update-lease', 'Updating lease...');
    try {
      const response = await leasesApi.update(id, leaseData);
      await fetchLeases(searchParams);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update lease';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('update-lease');
    }
  }, [fetchLeases, searchParams, startLoading, stopLoading]);

  const endLease = useCallback(async (id: number, data: EndLeaseRequest = {}): Promise<LeaseWithDetails> => {
    startLoading('end-lease', 'Ending lease...');
    try {
      const response = await leasesApi.end(id, data);
      await fetchLeases(searchParams);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end lease';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('end-lease');
    }
  }, [fetchLeases, searchParams, startLoading, stopLoading]);

  const deleteLease = useCallback(async (id: number): Promise<void> => {
    startLoading('delete-lease', 'Deleting lease...');
    try {
      await leasesApi.delete(id);
      await fetchLeases(searchParams);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete lease';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('delete-lease');
    }
  }, [fetchLeases, searchParams, startLoading, stopLoading]);

  const getLeaseById = useCallback(async (id: number): Promise<LeaseWithDetails | null> => {
    startLoading('get-lease-by-id', 'Loading lease...');
    try {
      return await leasesApi.getById(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch lease');
      return null;
    } finally {
      stopLoading('get-lease-by-id');
    }
  }, [startLoading, stopLoading]);

  return {
    leases,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    createLease,
    updateLease,
    endLease,
    deleteLease,
    getLeaseById,
    refetch: fetchLeases,
    globalLoading,
    loadingMessage
  };
};
